/**
 * ExchangeRateService - Service pour les taux de change
 * Utilise l'API Frankfurter (gratuite, sans clé API)
 * @class ExchangeRateService
 */
import { ApiService } from './ApiService.js';
import { Currency } from '../models/Currency.js';

export class ExchangeRateService extends ApiService {
    /**
     * Crée une instance d'ExchangeRateService
     */
    constructor() {
        // API Frankfurter - gratuite et sans authentification
        super('https://api.frankfurter.app');
        this._ratesCache = new Map();
        this._cacheDuration = 3600000; // 1 heure en millisecondes
        this._supportedCurrencies = null;
    }

    /**
     * Récupère les devises supportées
     * @returns {Promise<Object>} Liste des devises supportées
     */
    async getSupportedCurrencies() {
        if (this._supportedCurrencies) {
            return this._supportedCurrencies;
        }

        try {
            const data = await this.get('/currencies');
            this._supportedCurrencies = data;
            return data;
        } catch (error) {
            console.error('Error fetching supported currencies:', error);
            throw error;
        }
    }

    /**
     * Vérifie si une devise est supportée
     * @param {string} currencyCode - Code de la devise
     * @returns {Promise<boolean>} True si supportée
     */
    async isCurrencySupported(currencyCode) {
        const currencies = await this.getSupportedCurrencies();
        return currencyCode.toUpperCase() in currencies;
    }

    /**
     * Récupère les taux de change pour une devise de base
     * @param {string} baseCurrency - Code de la devise de base
     * @returns {Promise<Object>} Taux de change
     */
    async getRates(baseCurrency = 'EUR') {
        const cacheKey = baseCurrency.toUpperCase();
        const cached = this._ratesCache.get(cacheKey);

        // Vérifie si le cache est valide
        if (cached && (Date.now() - cached.timestamp) < this._cacheDuration) {
            return cached.data;
        }

        try {
            const data = await this.get('/latest', { from: baseCurrency });

            // Met en cache
            this._ratesCache.set(cacheKey, {
                data: data,
                timestamp: Date.now()
            });

            return data;
        } catch (error) {
            console.error(`Error fetching rates for ${baseCurrency}:`, error);
            throw error;
        }
    }

    /**
     * Récupère le taux de change entre deux devises
     * @param {string} from - Devise source
     * @param {string} to - Devise cible
     * @returns {Promise<number>} Taux de change
     */
    async getRate(from, to) {
        if (from.toUpperCase() === to.toUpperCase()) {
            return 1;
        }

        try {
            const data = await this.get('/latest', {
                from: from.toUpperCase(),
                to: to.toUpperCase()
            });

            return data.rates[to.toUpperCase()] || 1;
        } catch (error) {
            console.error(`Error fetching rate ${from} to ${to}:`, error);
            throw error;
        }
    }

    /**
     * Convertit un montant d'une devise à une autre
     * @param {number} amount - Montant à convertir
     * @param {string} from - Devise source
     * @param {string} to - Devise cible
     * @returns {Promise<Object>} Résultat de la conversion
     */
    async convert(amount, from, to) {
        try {
            const data = await this.get('/latest', {
                amount: amount,
                from: from.toUpperCase(),
                to: to.toUpperCase()
            });

            return {
                amount: amount,
                from: from.toUpperCase(),
                to: to.toUpperCase(),
                rate: data.rates[to.toUpperCase()] / amount,
                result: data.rates[to.toUpperCase()],
                date: data.date
            };
        } catch (error) {
            console.error(`Error converting ${amount} ${from} to ${to}:`, error);
            throw error;
        }
    }

    /**
     * Récupère l'historique des taux sur une période
     * @param {string} from - Devise source
     * @param {string} to - Devise cible
     * @param {string} startDate - Date de début (YYYY-MM-DD)
     * @param {string} endDate - Date de fin (YYYY-MM-DD)
     * @returns {Promise<Object>} Historique des taux
     */
    async getHistoricalRates(from, to, startDate, endDate) {
        try {
            const data = await this.get(`/${startDate}..${endDate}`, {
                from: from.toUpperCase(),
                to: to.toUpperCase()
            });

            return data;
        } catch (error) {
            console.error('Error fetching historical rates:', error);
            throw error;
        }
    }

    /**
     * Crée un objet Currency avec son taux de change
     * @param {Object} currencyData - Données de la devise
     * @param {string} baseCurrency - Devise de base
     * @returns {Promise<Currency>} Instance de Currency
     */
    async createCurrencyWithRate(currencyData, baseCurrency = 'EUR') {
        let rate = 1;

        try {
            // Vérifie si la devise est supportée par l'API
            const isSupported = await this.isCurrencySupported(currencyData.code);
            
            if (isSupported && currencyData.code !== baseCurrency) {
                rate = await this.getRate(baseCurrency, currencyData.code);
            }
        } catch (error) {
            console.warn(`Could not get rate for ${currencyData.code}, using 1:`, error);
        }

        return Currency.fromCountryData(currencyData, rate, baseCurrency);
    }

    /**
     * Vide le cache des taux
     */
    clearCache() {
        this._ratesCache.clear();
    }
}
